import { useMemo } from 'react';

import { Input, Select, Autocomplete } from '@new-components/elements';
import { useFormContext } from '@hooks/react-hook-form';
import {
  useRGetDistricts,
  useRGetProvinces,
  useRGetRegencies,
  useRGetSubdistricts,
} from '@repositories/common';
import {
  ApplicationDataResponse,
  useRGetApplicationData,
} from '@repositories/new-application';

import ApplicationCard from '../../../../../application-card';
import SectionTitle from '../../../../../section-title';

import { DomicileProps } from './types';
import { resetRelatedFieldOnAddressOptionChange } from './utils';
import { MapIcon } from '@new-components/shapes';

const Domicile = (props: DomicileProps) => {
  const { readonly } = props;

  const {
    fieldsRef,
    formState: { isSubmitting, isSubmitSuccessful },
    setValue,
    watch,
  } = useFormContext();

  const province = watch('addressProvince');
  const regency = watch('addressRegency');
  const district = watch('addressDistrict');

  const {
    data: provincesData,
    isError: isErrorGetProvinces,
    refetch: refetchGetProvinces,
    isInitialLoading: isLoadingGetProvinces,
  } = useRGetProvinces({
    enabled: true,
  });

  const {
    data: regenciesData,
    isError: isErrorGetRegencies,
    refetch: refetchGetRegencies,
    isInitialLoading: isLoadingGetRegencies,
  } = useRGetRegencies({
    enabled: Boolean(province),
    province,
  });

  const {
    data: districtsData,
    isError: isErrorGetDistricts,
    refetch: refetchGetDistricts,
    isInitialLoading: isLoadingGetDistricts,
  } = useRGetDistricts({
    enabled: Boolean(province) && Boolean(regency),
    province,
    city: regency,
  });

  const {
    data: subdistrictsData,
    isError: isErrorGetSubdistricts,
    refetch: refetchGetSubdistricts,
    isInitialLoading: isLoadingGetSubdistricts,
  } = useRGetSubdistricts({
    enabled: Boolean(province) && Boolean(regency) && Boolean(district),
    province,
    city: regency,
    district,
  });

  const {
    data: applicationData,
    isError: isErrorGetApplicationData,
    refetch: refetchGetApplicationData,
    isLoading: isLoadingGetApplicationData,
  } = useRGetApplicationData<ApplicationDataResponse<1>>({
    page: 1,
  });

  const dependentOptions = useMemo(() => {
    return Array.from({ length: 10 }).map((_, i) => ({ value: i, label: i }));
  }, []);

  return (
    <ApplicationCard className='section-domicile'>
      <SectionTitle title='Informasi Domisili' />
      <Input
        ref={(e) => (fieldsRef.current['address'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='address'
        label='Alamat Tempat Tinggal Saat Ini'
        placeholder='Alamat Tempat Tinggal Saat Ini'
        InputProps={{
          endAdornment: <MapIcon width={20} height={20} />,
        }}
      />
      <Autocomplete
        ref={(e) => (fieldsRef.current['addressProvince'] = e)}
        name='addressProvince'
        label='Provinsi'
        onFocus={() => isErrorGetProvinces && refetchGetProvinces()}
        disabled={
          readonly ||
          isLoadingGetProvinces ||
          (isSubmitting && !isSubmitSuccessful)
        }
        options={provincesData?.data || []}
        onChange={() => {
          resetRelatedFieldOnAddressOptionChange(
            [
              'addressRegency',
              'addressDistrict',
              'addressSubdistrict',
              'addressZipcode',
            ],
            setValue,
          );
        }}
      />
      <Autocomplete
        ref={(e) => (fieldsRef.current['addressRegency'] = e)}
        name='addressRegency'
        label='Kabupaten'
        onFocus={() =>
          (isErrorGetRegencies || !regenciesData?.data) && refetchGetRegencies()
        }
        disabled={
          readonly ||
          isLoadingGetRegencies ||
          (isSubmitting && !isSubmitSuccessful)
        }
        options={regenciesData?.data || []}
        onChange={() => {
          resetRelatedFieldOnAddressOptionChange(
            ['addressDistrict', 'addressSubdistrict', 'addressZipcode'],
            setValue,
          );
        }}
      />
      <Select
        ref={(e) => (fieldsRef.current['addressDistrict'] = e)}
        name='addressDistrict'
        label='Kecamatan'
        onFocus={() =>
          (isErrorGetDistricts || !districtsData?.data) && refetchGetDistricts()
        }
        disabled={
          readonly ||
          isLoadingGetDistricts ||
          (isSubmitting && !isSubmitSuccessful)
        }
        options={districtsData?.data}
        onChange={() => {
          resetRelatedFieldOnAddressOptionChange(
            ['addressSubdistrict', 'addressZipcode'],
            setValue,
          );
        }}
      />
      <Select
        ref={(e) => (fieldsRef.current['addressSubdistrict'] = e)}
        name='addressSubdistrict'
        label='Kelurahan'
        onFocus={() =>
          (isErrorGetSubdistricts || !subdistrictsData?.data) &&
          refetchGetSubdistricts()
        }
        disabled={
          readonly ||
          isLoadingGetSubdistricts ||
          (isSubmitting && !isSubmitSuccessful)
        }
        options={subdistrictsData?.data.map(({ subDistrict }) => ({
          label: subDistrict,
          value: subDistrict,
        }))}
        onChange={(e) => {
          setValue(
            'addressZipcode',
            subdistrictsData?.data.find(
              ({ subDistrict }) => subDistrict === e.target.value,
            )?.zipcode,
            { shouldValidate: true },
          );
        }}
      />
      <Input
        ref={(e) => (fieldsRef.current['addressZipcode'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='addressZipcode'
        label='Kodepos'
      />
      <Select
        ref={(e) => (fieldsRef.current['homeStatus'] = e)}
        name='homeStatus'
        label='Status Kepemilikan Rumah'
        placeholder='pilih'
        disabled={
          readonly ||
          isLoadingGetApplicationData ||
          (isSubmitting && !isSubmitSuccessful)
        }
        onFocus={() => isErrorGetApplicationData && refetchGetApplicationData()}
        options={applicationData?.data.homeStatuses as string[]}
      />
      <div className='row-field'>
        <Select
          ref={(e) => (fieldsRef.current['maritalStatus'] = e)}
          name='maritalStatus'
          label='Status Sipil'
          disabled={
            readonly ||
            isLoadingGetApplicationData ||
            (isSubmitting && !isSubmitSuccessful)
          }
          onFocus={() =>
            isErrorGetApplicationData && refetchGetApplicationData()
          }
          options={applicationData?.data.maritalStatuses}
        />
        <Select
          ref={(e) => (fieldsRef.current['totalDependent'] = e)}
          disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
          name='totalDependent'
          label='Jumlah Tanggungan'
          options={dependentOptions}
        />
      </div>
    </ApplicationCard>
  );
};

export default Domicile;
